import { inject, injectable } from 'tsyringe';
import { BaseViewModel } from '@/shared/viewmodels/BaseViewModel';
import { IAnalyticsGatewayToken, type IAnalyticsGateway } from '@/shared/analytics/AnalyticsGateway';
import { findTopicInSurface } from './AnalyticsSurface';
import { SurfaceRegistry } from './SurfaceRegistry';
import { deriveCallouts, type TopicCallout } from './TopicNarration';
import { PinnedTilesStore } from './PinnedTilesStore';
import { definitionFromTile } from './PinnedTileTypes';
import type { TopicDefinition } from './TopicCatalogTypes';
import type { AdHocAggregateResult, AdHocTrendResult, AnalyticsFilter, AnalyticsMetric, AnomalyResult } from '@/shared/analytics/AnalyticsTypes';
import { humanUser } from '@/shared/analytics/MetricFormatting';

const TOPIC_METRICS: AnalyticsMetric[] = ['turns', 'sessions', 'users', 'outputTokens', 'totalTokens', 'avgOutputTokens', 'avgConfidence'];

/**
 * The full topic page lists every flagged anomaly (paginated client-side),
 * overriding the small tile-preview cap so the ranked list reconciles with the
 * summary's total count.
 */
const TOPIC_ANOMALY_LIMIT = 1000;

/** Maximum users shown in a `user-trend-list` viz, after slope ranking. */
const USER_TREND_LIMIT = 25;

/** One accelerating/decaying user in a `user-trend-list` viz. */
export interface UserTrendRow {
    userId: string;
    label: string;
    /** Bucket-ordered headline-metric values, driving the sparkline. */
    series: number[];
    /** Most recent bucket value. */
    current: number;
    /** Percent change from the first bucket to the last (may be negative). */
    deltaPct: number;
}

/**
 * L1 view model. Loads the primary breakdown for a topic (driving the header,
 * viz, and narration) and, depending on viz kind, the two-field cross-tab
 * (matrix) or the time series (trend).
 */
@injectable()
export class SurfaceTopicViewModel extends BaseViewModel {
    definition: TopicDefinition | null = null;
    primary: AdHocAggregateResult | null = null;
    matrix: AdHocAggregateResult | null = null;
    trend: AdHocTrendResult | null = null;
    anomalies: AnomalyResult | null = null;
    userTrends: UserTrendRow[] = [];
    callouts: TopicCallout[] = [];
    notFound = false;
    loading = false;
    errorMessage: string | null = null;

    constructor(
        @inject(IAnalyticsGatewayToken) private gateway: IAnalyticsGateway,
        @inject(PinnedTilesStore) private pinnedTiles: PinnedTilesStore,
        @inject(SurfaceRegistry) private registry: SurfaceRegistry,
    ) {
        super();
    }

    async load(surfaceId: string, topicId: string | undefined, filters: AnalyticsFilter[] = []): Promise<void> {
        const surface = this.registry.find(surfaceId);
        const definition = surface === undefined ? undefined : (findTopicInSurface(surface, topicId) ?? this.findPinnedDefinition(surfaceId, topicId));
        if (definition === undefined) {
            this.notFound = true;
            this.notifyChange();
            return;
        }
        this.notFound = false;
        this.definition = definition;
        this.primary = null;
        this.matrix = null;
        this.trend = null;
        this.anomalies = null;
        this.userTrends = [];
        this.callouts = [];
        this.errorMessage = null;
        if (definition.comingSoon === true) {
            this.loading = false;
            this.notifyChange();
            return;
        }
        this.loading = true;
        this.notifyChange();
        try {
            const primary = await this.gateway.aggregate({
                source: 'interactions',
                groupBy: [definition.primaryField],
                metrics: TOPIC_METRICS,
                orderBy: { metric: definition.headlineMetric, direction: 'desc' },
                filters,
            });
            this.primary = primary;
            this.callouts = deriveCallouts(definition, primary);

            if (definition.vizKind === 'matrix' && definition.secondaryField !== undefined) {
                this.matrix = await this.gateway.aggregate({
                    source: 'interactions',
                    groupBy: [definition.primaryField, definition.secondaryField],
                    metrics: ['turns', 'totalTokens', 'avgConfidence', 'allocatedCostUsd', 'avgModelFit'],
                    filters,
                });
            } else if (definition.vizKind === 'trend') {
                this.trend = await this.gateway.trend({
                    source: 'interactions',
                    bucket: definition.timeBucket ?? 'week',
                    metrics: ['turns', 'totalTokens', 'allocatedCostUsd', 'avgModelFit'],
                    filters,
                });
            } else if (definition.vizKind === 'time-matrix') {
                this.trend = await this.gateway.trend({
                    source: 'interactions',
                    bucket: definition.timeBucket ?? 'week',
                    metrics: [definition.headlineMetric],
                    groupBy: definition.primaryField,
                    filters,
                });
            } else if (definition.vizKind === 'alerts' && definition.anomalyKind !== undefined) {
                this.anomalies = await this.gateway.anomalies({ kind: definition.anomalyKind, filters, limit: TOPIC_ANOMALY_LIMIT });
            } else if (definition.vizKind === 'user-trend-list') {
                const trend = await this.gateway.trend({
                    source: 'interactions',
                    bucket: definition.timeBucket ?? 'week',
                    metrics: [definition.headlineMetric],
                    groupBy: 'user_id',
                    filters,
                });
                this.userTrends = buildUserTrends(trend, definition.headlineMetric, definition.trendDirection ?? 'rising');
            }
        } catch (err) {
            this.errorMessage = err instanceof Error ? err.message : String(err);
        } finally {
            this.loading = false;
            this.notifyChange();
        }
    }

    private findPinnedDefinition(surfaceId: string, topicId: string | undefined): TopicDefinition | undefined {
        if (topicId === undefined) return undefined;
        const tile = this.pinnedTiles.tilesFor(surfaceId).find((entry) => entry.id === topicId);
        return tile === undefined ? undefined : definitionFromTile(tile);
    }
}

/** Least-squares slope of a series over its bucket index — the sign says rising vs. declining. */
function seriesSlope(series: number[]): number {
    const n = series.length;
    const meanX = (n - 1) / 2;
    const meanY = series.reduce((sum, v) => sum + v, 0) / n;
    let cov = 0;
    let varX = 0;
    for (let i = 0; i < n; i += 1) {
        cov += (i - meanX) * ((series[i] ?? 0) - meanY);
        varX += (i - meanX) ** 2;
    }
    return varX === 0 ? 0 : cov / varX;
}

/**
 * Turns a `user_id`-grouped trend into a ranked list of accelerating or decaying
 * users. Each user's bucketed metric series drives a slope; positive slopes are
 * "rising", negative are "declining". Ranked by slope magnitude, capped.
 */
function buildUserTrends(trend: AdHocTrendResult, metric: AnalyticsMetric, direction: 'rising' | 'declining'): UserTrendRow[] {
    const byUser = new Map<string, { bucket: string; value: number }[]>();
    for (const point of trend.points) {
        const userId = point.key;
        if (userId === undefined || userId === '—') continue;
        const bucketValues = byUser.get(userId) ?? [];
        bucketValues.push({ bucket: point.bucket, value: point.metrics[metric] });
        byUser.set(userId, bucketValues);
    }

    const rows: (UserTrendRow & { slope: number })[] = [];
    for (const [userId, points] of byUser) {
        if (points.length < 2) continue;
        points.sort((a, b) => a.bucket.localeCompare(b.bucket));
        const series = points.map((p) => p.value);
        const slope = seriesSlope(series);
        if (direction === 'rising' ? slope <= 0 : slope >= 0) continue;
        const first = series[0] ?? 0;
        const current = series[series.length - 1] ?? 0;
        const deltaPct = first > 0 ? ((current - first) / first) * 100 : current > 0 ? 100 : 0;
        rows.push({ userId, label: humanUser(userId), series, current, deltaPct, slope });
    }

    return rows
        .sort((a, b) => Math.abs(b.slope) - Math.abs(a.slope))
        .slice(0, USER_TREND_LIMIT)
        .map((r) => ({ userId: r.userId, label: r.label, series: r.series, current: r.current, deltaPct: r.deltaPct }));
}
